import Observable from '../helpers/observerable'
import ApiManager from './apiManager.js'
import PhoneLogManager from './phoneLogManager.js'
import { getDeviceInfoBasedOnBrowser } from './utilitys'

export default class WebrtcManager {
    static instacne: WebrtcManager = null

    static getInstance({ iceServers }) {
        if (!this.instacne) {
            this.instacne = new WebrtcManager({
                iceServers,
            })
        }
        return this.instacne
    }

    constructor({ iceServers }) {
        this.pcList = {}
        this.observable = new Observable()
        this.apiManager = ApiManager.getInstance()

        const _iceServers = [
            {
                urls: ['stun:stun.l.google.com:19302'],
            },
        ]
        _iceServers.push(iceServers[0])
        _iceServers.push(iceServers[1]) // only need udp turn server (like App)

        this.peerConnectionConfig = {
            iceServers: _iceServers,
            // iceTransportPolicy: 'relay'
        }
    }

    async updateStream({ videoStream, isAudio, isFree }) {
        if (!videoStream) {
            return
        }

        const tracks = videoStream.getTracks()

        for (const viewerJid of Object.keys(this.pcList)) {
            const senderList = this.pcList[viewerJid].pc.getSenders()

            for (const track of tracks) {
                let sender = senderList.find(function (s) {
                    return s.track && s.track.kind === track.kind
                })

                if (track.kind === 'video') {
                    if (sender) {
                        await sender.replaceTrack(track)
                    } else {
                        await this.pcList[viewerJid].pc.addTrack(
                            track,
                            videoStream
                        )
                    }
                }
                if (track.kind === 'audio') {
                    if (sender) {
                        // we have to use enable/disable track instead of removing it
                        // if we remove track and want to add back, we have to renegotiate
                        if (isAudio === true) {
                            videoStream.getAudioTracks()[0].enabled = true
                            await sender.replaceTrack(track)
                        } else {
                            videoStream.getAudioTracks()[0].enabled = false
                        }
                    }
                }
            }

            const videoStreamSettings = videoStream
                .getVideoTracks()[0]
                .getSettings()
            let videoWidth = videoStreamSettings.width

            await restrictBitrate({
                pc: this.pcList[viewerJid].pc,
                isFree,
                videoWidth,
            })
        }
    }

    async toggleIsRelay(isRelay) {
        if (isRelay) {
            this.peerConnectionConfig['iceTransportPolicy'] = 'relay'
        } else {
            delete this.peerConnectionConfig['iceTransportPolicy']
        }
    }

    async disconnectIfLiveViewerOffline(viewerJid) {
        if (this.pcList[viewerJid]) {
            this.pcList[viewerJid].disconnect()
            delete this.pcList[viewerJid]
            return true
        }
        return false
    }

    async setOffer({
        offer,
        videoStream,
        viewerJid,
        isWebviewer,
        isFree,
        isPreview,
        uniqueId,
        isAudio,
        email,
        getMotionStatus,
    }) {
        if (this.pcList[viewerJid]) {
            this.pcList[viewerJid].disconnect()
            delete this.pcList[viewerJid]
        }

        const ssid = parseSSIDFromOffer(offer)

        if (!isPreview) {
            this.observable.notify({
                key: 'terminateViewerWebrtcConnection',
                viewerJid,
                close: 1,
                ssid: ssid,
            })
            throw 'CameraDisable'
        }

        // reject
        if (Object.keys(this.pcList).length > 0) {
            this.observable.notify({
                key: 'terminateViewerWebrtcConnection',
                viewerJid,
                close: 5,
                ssid: ssid,
            })
            throw 'DuplicateViewer'
        }

        // check
        const viewerUniqueId = /^(.*)@/.exec(viewerJid)[1]
        if (uniqueId !== viewerUniqueId) {
            const isTrustCircle = await this.apiManager.checkTrustCircle({
                sharedUniqueId: viewerUniqueId,
            })

            if (!isTrustCircle) {
                this.observable.notify({
                    key: 'terminateViewerWebrtcConnection',
                    viewerJid,
                    close: 4,
                    ssid: ssid,
                })
                throw 'AccessDenied'
            }
        }

        this.connect({
            viewerJid,
            isFree,
            isAudio,
            email,
            getMotionStatus,
            videoStream,
        })

        return this.pcList[viewerJid].setOffer({
            offer,
            isWebviewer,
            ssid,
            email,
        })
    }

    connect({
        viewerJid,
        isFree,
        isAudio,
        email,
        getMotionStatus,
        videoStream,
    }) {
        const webrtcConnection = new WebrtcConnection({
            viewerJid,
            peerConnectionConfig: this.peerConnectionConfig,
            isFree,
            isAudio,
            email,
            getMotionStatus,
            videoStream,
        })
        this.pcList[viewerJid] = webrtcConnection

        webrtcConnection.observable.subscribe((data) => {
            this.observable.notify({
                to: viewerJid,
                ...data,
            })
        })
    }

    async checkLiveViewerSharePermission({ uniqueId }) {
        // check
        Object.keys(this.pcList).map(async (viewerJid) => {
            const ssid = this.pcList[viewerJid].ssid
            const viewerUniqueId = /^(.*)@/.exec(viewerJid)[1]
            if (uniqueId !== viewerUniqueId) {
                const isTrustCircle = await this.apiManager.checkTrustCircle({
                    sharedUniqueId: viewerUniqueId,
                })

                if (!isTrustCircle) {
                    this.observable.notify({
                        key: 'terminateViewerWebrtcConnection',
                        viewerJid,
                        close: 4,
                        ssid: ssid,
                    })
                    throw 'AccessDenied'
                }
            }
        })
    }

    addIceCandidate({ viewerJid, candidate }) {
        if (!this.pcList[viewerJid]) {
            // this.connect({
            //     viewerJid
            // })
        } else {
            this.pcList[viewerJid].addIceCandidate(candidate)
        }
    }

    disconnect(viewerJid) {
        if (this.pcList[viewerJid]) {
            this.pcList[viewerJid].disconnect()
            delete this.pcList[viewerJid]
        }
    }

    getCurrentLiveViewerList() {
        return this.pcList
    }
    getCurrentLiveViewerCount() {
        return Object.keys(this.pcList).length
    }

    disconnectLiveViewers({ close = 1 }) {
        for (const viewerJid in this.pcList) {
            this.observable.notify({
                key: 'terminateViewerWebrtcConnection',
                viewerJid,
                close,
                ssid: this.pcList[viewerJid].ssid,
            })
            this.pcList[viewerJid].disconnect()
            delete this.pcList[viewerJid]
        }
    }

    close(webrtcSubscriber) {
        for (const viewerJid in this.pcList) {
            this.disconnect(viewerJid)
        }
        this.observable.unsubscribe(webrtcSubscriber)
    }
}

class WebrtcConnection {
    constructor({
        videoStream,
        viewerJid,
        peerConnectionConfig,
        isFree,
        isAudio,
        email,
        getMotionStatus,
    }) {
        const peerConnectionOption = {
            optional: [
                {
                    RtpDataChannels: false,
                },
            ],
        }
        this.pc = new RTCPeerConnection(
            peerConnectionConfig,
            peerConnectionOption
        )
        this.observable = new Observable()
        // iceCandidate should be set after set Answer
        this.isSetAnswer = false
        this.cacheIceCandidateList = []
        this.tracks = []
        this.isFree = isFree
        this.isAudio = isAudio
        this.getMotionStatus = getMotionStatus
        this.videoStream = videoStream
        const videoStreamSettings = videoStream
            .getVideoTracks()[0]
            .getSettings()
        this.videoWidth = videoStreamSettings.width
        this.initialConnection = false

        this.pc.onconnectionstatechange = async (event) => {
            if (this.pc.connectionState === 'connected') {
                this.groupID =
                    email.split('@')[0] +
                    new Date().getTime() +
                    (Math.floor(Math.random() * 9999999) + 1)
                this.phoneLogManager = new PhoneLogManager({
                    pc: this.pc,
                    groupID: this.groupID,
                    getMotionStatus,
                })
                this.phoneLogManager.startMonitor()

                const results = await this.pc.getStats(null)
                let activeCandidatePair = null
                results.forEach((report) => {
                    if (report.type === 'transport') {
                        activeCandidatePair = results.get(
                            report.selectedCandidatePairId
                        )
                    }
                })

                if (
                    activeCandidatePair &&
                    activeCandidatePair.remoteCandidateId
                ) {
                    let remoteCandidate = results.get(
                        activeCandidatePair.remoteCandidateId
                    )
                    let localCandidate = results.get(
                        activeCandidatePair.localCandidateId
                    )

                    window.logManager.logForSure(
                        `[Live] localCandidate Type: ${localCandidate?.candidateType}, remoteCandidate Type: ${remoteCandidate?.candidateType}`
                    )
                    const upgradeTo720p =
                        localCandidate &&
                        localCandidate.candidateType === 'host' &&
                        remoteCandidate &&
                        remoteCandidate.candidateType === 'host' &&
                        this.isFree === false &&
                        this.initialConnection === false

                    const downgradeTo360p =
                        ((localCandidate &&
                            localCandidate.candidateType === 'relay') ||
                            (remoteCandidate &&
                                remoteCandidate.candidateType === 'relay')) &&
                        this.initialConnection === false

                    let defaultWidth = this.videoWidth
                    if (upgradeTo720p) {
                        defaultWidth = 1280
                    }
                    if (downgradeTo360p) {
                        defaultWidth = 640
                    }

                    this.initialConnection = true
                    this.observable.notify({
                        key: 'connected',
                        liveWidth: defaultWidth,
                        groupID: this.groupID,
                        viewerJid,
                    })
                }

                await restrictBitrate({
                    pc: this.pc,
                    isFree: this.isFree,
                    videoWidth: this.videoWidth,
                })
            }
        }

        this.pc.onicecandidate = (event) => {
            if (event.candidate && event.candidate.candidate) {
                // Send the candidate to the remote peer
                this.observable.notify({
                    key: 'sendCandidate',
                    viewerJid,
                    candidate: JSON.stringify({
                        type: 'candidate',
                        sdpMid: event.candidate.sdpMid,
                        candidate: event.candidate.candidate,
                    }),
                })
            } else {
                // All ICE candidates have been sent
            }
        }

        this.pc.ontrack = (event) => {
            // [TODO] two way talk
        }
    }

    disconnect() {
        this.pc.close()
        if (this.phoneLogManager) {
            this.phoneLogManager.endMonitor()
        }
    }

    async setOffer({ offer, isWebviewer, ssid, email }) {
        try {
            this.tracks = this.videoStream.getTracks()

            const remoteOffer = new RTCSessionDescription({
                type: 'offer',
                sdp: offer,
            })
            await this.pc.setRemoteDescription(remoteOffer)

            this.ssid = ssid

            const audioTrack = this.videoStream.getAudioTracks()[0]
            if (this.isAudio === false && audioTrack) {
                audioTrack.enabled = false
            }
            await Promise.all(
                this.tracks.map(
                    async (track) =>
                        await this.pc.addTrack(track, this.videoStream)
                )
            )

            let answer = await this.pc.createAnswer()
            if (!isWebviewer) {
                const replaceAnswerSDP = replaceSDPOrigin({
                    sdp: answer.sdp,
                    ssid,
                })
                answer.sdp = replaceAnswerSDP
            }

            await this.pc.setLocalDescription(answer)

            this.isSetAnswer = true
            this.cacheIceCandidateList.map(async (candidate) => {
                await this.pc.addIceCandidate(new RTCIceCandidate(candidate))
            })
            this.cacheIceCandidateList = []

            // send back as answer
            return {
                sdp: answer.sdp,
                groupID: this.groupID,
            }
        } catch (error) {
            window.logManager.error(error)
        }
    }

    async addIceCandidate(candidate) {
        if (this.isSetAnswer) {
            try {
                await this.pc.addIceCandidate(new RTCIceCandidate(candidate))
            } catch (error) {}
        } else {
            this.cacheIceCandidateList.push(candidate)
        }
    }
}

function parseSSIDFromOffer(offer) {
    const extractSSIdRegex = new RegExp(/o=\- ([0-9]*) /g)
    const result = extractSSIdRegex.exec(offer)
    if (result && result[1]) {
        return result[1]
    }
    return '1'
}

function replaceSDPOrigin({ sdp, ssid }) {
    const extractSDPRegex = /o\=([a-zA-Z0-9\._-]*) ([0-9]*)/g
    if (extractSDPRegex.test(sdp)) {
        const replacedOrigin = `o=- ${ssid}`
        sdp = sdp.replace(extractSDPRegex, replacedOrigin)
    }

    return sdp
}

function reserveH264Codec(sdp) {
    const mlineList = sdp.split('\n')
    for (let i = 0; i < mlineList.length; i++) {
        if (
            /a=rtpmap:/.test(mlineList[i]) &&
            /(VP9)|(VP8)|(rtx)/.test(mlineList[i])
        ) {
            mlineList.splice(i, 1)
            while (!/a=ssrc/.test(mlineList[i]) && mlineList.length > i) {
                mlineList.splice(i, 1)
            }
            ;`a=rtpmap:125 H264/90000
a=rtcp-fb:125 goog-remb
a=rtcp-fb:125 transport-cc
a=rtcp-fb:125 ccm fir
a=rtcp-fb:125 nack
a=rtcp-fb:125 nack pli
a=fmtp:125 level-asymmetry-allowed=1;packetization-mode=1;profile-level-id=42e01f
`
                .split('\n')
                .reverse()
                .map((line) => mlineList.splice(i, 0, line))
        }
    }

    const result = mlineList.join('\n')
    return result
}

async function restrictBitrate({ pc, isFree, videoWidth }) {
    const bandwidth = getBandwitdh({ isFree, videoWidth })
    const { osVersion } = getDeviceInfoBasedOnBrowser()
    const browserVersion = ~~osVersion.split('_')[1]

    const senderList = pc.getSenders()
    for (const sender of senderList) {
        if (sender.track && sender.track.kind === 'video') {
            const parameters = sender.getParameters()
            if (!parameters.encodings) {
                parameters.encodings = [{}]
            }
            if (!parameters.encodings[0]) {
                parameters.encodings[0] = {}
            }
            parameters.encodings[0].maxBitrate = bandwidth * 1000
            if (browserVersion >= 83) {
                parameters.degradationPreference = 'maintain-resolution'
            }
            try {
                await sender.setParameters(parameters)
            } catch (error) {
                window.logManager.error(error)
            }
        }
    }
}
function getBandwitdh({ isFree, videoWidth }) {
    let bandwidth = 200
    if (!isFree) {
        switch (videoWidth) {
            case 320:
                bandwidth = 200
                break
            case 640:
                bandwidth = 350
                break
            case 1280:
                bandwidth = 800
                break
            default:
                bandwidth = 350
        }
    } else {
        bandwidth = 200
    }
    return bandwidth
}
